/**
 * Runs the "extract print" / generate-edits flow: fetch image, build prompt, call API.
 * Returns result for the component to apply to state.
 */
import prompts from '@/assets/mizodia/custom-print-prompts.json'
import { generateEdits, generateEditsAndProcess } from '../../services/pdfGenerationService'
import {
  EDITS_PER_REQUEST,
  defaultModelValueForPrintType,
  GEMINI_25_FLASH_MODEL_VALUE,
} from './constants'
import { nanoBananaFilenameFromImageUrl } from './editsGalleryHelpers'

function effectivePrintType(printType, printTypes) {
  return typeof printType === 'number'
    ? (printTypes[printType]?.value || 'fingerprint')
    : printType
}

export async function runExtractPrint({
  imageUrl,
  selectedTab,
  printType,
  printTypes,
  prompt,
  model: customModel,
  models,
  orderNumber,
}) {
  const response = await fetch(imageUrl, { mode: 'cors' })
  if (!response.ok) throw new Error(`Failed to fetch image: ${response.status}`)
  const blob = await response.blob()
  const mimeType = blob.type || 'image/jpeg'

  const base64Image = await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      if (reader.result) resolve(String(reader.result).split(',')[1])
      else reject(new Error('Failed to read image'))
    }
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(blob)
  })

  const predefinedTab = selectedTab === 0
  const typeKey = effectivePrintType(printType, printTypes)
  const typeDefaultModel = defaultModelValueForPrintType(typeKey)
  const model =
    customModel ||
    (predefinedTab ? typeDefaultModel : models[0]?.value || typeDefaultModel)
  const typeCommand = predefinedTab ? (prompts[typeKey] || '') : (prompt || '')
  let fullPrompt = `${typeCommand}. `
  fullPrompt +=
    model === GEMINI_25_FLASH_MODEL_VALUE
      ? `Generate ${EDITS_PER_REQUEST} different edits for this.`
      : `You must generate ${EDITS_PER_REQUEST} edited versions of the input image, return exactly ${EDITS_PER_REQUEST} separate response parts, each containing only one edited image with no text, no combined images, no grouping; the output must be a sequence of ${EDITS_PER_REQUEST} independent image parts, and you must not put multiple images in one part or produce a collage.`

  const fetchEditsFn = ['baby_feet', 'baby_hands', 'pet_paw_print'].includes(typeKey) ? generateEditsAndProcess : generateEdits
  const apiResponse = await fetchEditsFn(model, fullPrompt, base64Image, mimeType, orderNumber)
  const data = apiResponse?.data
  if (!data?.success) throw new Error(data?.message || 'Generate edits failed')

  const editsRaw = data.data.edits || []
  const edits = editsRaw.map((edit) => edit.url)
  const nanoBananaEditMeta = editsRaw.map((edit) => ({
    url: edit.url,
    filename:
      (edit.filename && String(edit.filename).trim()) ||
      (edit.fileName && String(edit.fileName).trim()) ||
      nanoBananaFilenameFromImageUrl(edit.url),
  }))
  const responseTime =
    (parseInt(String(data.responseTime || '0').replace('ms', ''), 10) / 1000).toFixed(2) + 's'
  return {
    edits,
    nanoBananaEditMeta,
    responseTime,
    originalGeminiEdits: (data.data.originalGeminiEdits || []).map((e) => e.url),
  }
}
